import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { s3Api } from '../../api/s3';
import './MediaPreviewModal.css';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;

function MediaPreviewModal({
	visible,
	onClose,
	media,
	folders = [],
	tags = [],
	onUpdate,
	organizationId,
}) {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [mediaContent, setMediaContent] = useState(null);
	const [imageLoaded, setImageLoaded] = useState(false);
	const { orgData } = useAuth();

	const defaultLayoutPluginInstance = defaultLayoutPlugin();

	// Reset form and media content when media changes
	useEffect(() => {
		if (media) {
			form.setFieldsValue({
				name: media.name,
				folderId: media.folderId || 'root',
				tagId: media.tagId,
			});

			// Reset media content
			if (media.fileUrl) {
				setMediaContent(media.fileUrl);
			}
		} else {
			form.resetFields();
			setMediaContent(null);
		}
		setImageLoaded(false);
	}, [media, form]);

	// Clear everything when modal closes
	useEffect(() => {
		if (!visible) {
			setMediaContent(null);
			setImageLoaded(false);
			form.resetFields();
		}
	}, [visible, form]);

	const handleSubmit = async () => {
		setLoading(true);
		const values = await form.validateFields();

		// Initialize update data with current values
		const updateData = {
			name: values.name,
			organizationId: orgData.id,
			folderId: values.folderId === 'root' ? null : values.folderId,
			tagId: values.tagId || null,
			fileType: media.fileType,
			fileUrl: media.fileUrl,
		};

		try {
			// Check if folder changed
			if (values.folderId !== media.folderId) {
				// Format the filename
				const safeFileName = values.name
					.toLowerCase()
					.replace(/\s+/g, '-');

				// Move file using the new s3Api
				const newFileUrl = await s3Api.moveFile(
					organizationId,
					media.fileUrl,
					values.folderId,
					safeFileName
				);

				updateData.fileUrl = newFileUrl;
			}

			// Send update to API
			await onUpdate(media.id, updateData);
			message.success('Media updated successfully');
			onClose();
		} catch (error) {
			message.error('Failed to update media');
		} finally {
			setLoading(false);
		}
	};

	const renderPreview = () => {
		// Get the file type, handle both MIME type strings and extensions
		const fileType = media?.fileType?.toLowerCase() || '';

		// Get a display-friendly file type
		const getDisplayFileType = (type) => {
			if (type.startsWith('image/')) return type;
			switch (type) {
				case 'jpg':
				case 'jpeg':
					return 'image/jpeg';
				case 'png':
					return 'image/png';
				case 'gif':
					return 'image/gif';
				case 'pdf':
					return 'application/pdf';
				default:
					return type || 'Unknown';
			}
		};

		const isImage =
			fileType.startsWith('image/') ||
			fileType === 'jpg' ||
			fileType === 'jpeg' ||
			fileType === 'png' ||
			fileType === 'gif';

		if (isImage) {
			return (
				<div className='preview-section'>
					{!imageLoaded && (
						<div className='media-preview-ghost'>
							<div className='ghost-gradient'></div>
						</div>
					)}
					<img
						src={mediaContent}
						alt={media.name}
						className={`media-preview-image ${
							imageLoaded ? 'loaded' : ''
						}`}
						onLoad={(e) => {
							setImageLoaded(true);
						}}
						onError={(e) => {
							console.error('Image failed to load:', {
								src: e.target.src,
								error: e,
								fileType: getDisplayFileType(fileType),
							});
							message.error('Failed to load image preview');
						}}
					/>
				</div>
			);
		} else if (fileType.startsWith('video/')) {
			return (
				<video
					controls
					className='media-preview-video'>
					<source
						src={mediaContent}
						type={media.fileType}
					/>
					Your browser does not support the video tag.
				</video>
			);
		} else if (fileType.startsWith('audio/')) {
			return (
				<audio
					controls
					className='media-preview-audio'>
					<source
						src={mediaContent}
						type={media.fileType}
					/>
					Your browser does not support the audio tag.
				</audio>
			);
		} else if (fileType === 'application/pdf' || fileType === 'pdf') {
			return (
				<div className='media-preview-pdf'>
					{mediaContent && (
						<Worker workerUrl='https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'>
							<Viewer
								fileUrl={mediaContent}
								plugins={[defaultLayoutPluginInstance]}
								defaultScale={1}
								onError={(error) => {
									console.error('PDF viewer error:', error);
									message.error('Failed to load PDF preview');
								}}
							/>
						</Worker>
					)}
				</div>
			);
		} else {
			return (
				<div className='media-preview-default'>
					<span className='material-icons'>description</span>
					<p>{media.name}</p>
					<p className='file-type'>
						Type: {getDisplayFileType(fileType)}
					</p>
				</div>
			);
		}
	};

	return (
		<Modal
			title='Media Preview'
			open={visible}
			onCancel={onClose}
			onOk={handleSubmit}
			okText='Save Changes'
			confirmLoading={loading}
			width={800}
			className='common-modal media-preview-modal'>
			<div className='media-preview-container'>
				{media && (
					<>
						<div className='preview-section'>{renderPreview()}</div>
						<div className='edit-section'>
							<Form
								form={form}
								layout='vertical'
								initialValues={{
									name: media.name,
									folderId: media.folderId || 'root',
									tagId: media.tagId,
								}}>
								<Form.Item
									name='name'
									label='Name'
									rules={[
										{
											required: true,
											message: 'Please enter a name',
										},
									]}>
									<Input />
								</Form.Item>

								<Form.Item
									name='folderId'
									label='Location'>
									<Select>
										<Option value='root'>Root</Option>
										{folders.map((folder) => (
											<Option
												key={folder.id}
												value={folder.id}>
												{folder.name}
											</Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									name='tagId'
									label='Tag'>
									<Select allowClear>
										{tags.map((tag) => (
											<Option
												key={tag.id}
												value={tag.id}>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '8px',
													}}>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor:
																tag.color,
															borderRadius: 4,
														}}
													/>
													{tag.name}
												</div>
											</Option>
										))}
									</Select>
								</Form.Item>
							</Form>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
}

export default MediaPreviewModal;
